import React from 'react';
import { Button } from '@/components/ui/button';
import PaymentTermsSelector from '@/components/PaymentTermsSelector';

/**
 * "Convert to Project" confirmation modal. Captures the final agreed project
 * value (inc VAT) and the payment terms breakdown before turning a "Won" lead
 * into a Project record.
 */
export default function ConvertToProjectModal({
  lead,
  projectValue,
  setProjectValue,
  paymentTerms,
  setPaymentTerms,
  paymentTermsValid,
  setPaymentTermsValid,
  converting,
  onCancel,
  onConfirm,
}) {
  if (!lead) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold text-[#27292E] mb-1">Convert to Project</h3>
        <p className="text-xs text-gray-500 mb-5">
          Confirm the project details for <strong className="text-gray-800">{lead.customer_name}</strong>.
          {lead.latest_quote_reference && (
            <> Pre-filled from quote <code className="font-mono">{lead.latest_quote_reference}</code>.</>
          )}
        </p>

        <div className="mb-5">
          <label htmlFor="convert-value" className="text-sm font-semibold text-gray-700">
            Agreed project value (inc VAT) <span className="text-rose-500">*</span>
          </label>
          <p className="text-[11px] text-gray-500 mb-1">
            Change this from the quoted figure if the customer agreed a different price.
          </p>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">£</span>
            <input
              id="convert-value"
              data-testid="convert-project-value"
              type="number"
              step="0.01"
              min="0"
              value={projectValue}
              onChange={(e) => setProjectValue(e.target.value)}
              placeholder="0.00"
              className="w-full pl-7 pr-3 h-11 border border-gray-200 rounded-xl text-lg font-semibold focus:outline-none focus:border-[#006400]"
            />
          </div>
        </div>

        <div className="mb-5">
          <PaymentTermsSelector
            grandTotal={parseFloat(projectValue) || 0}
            value={paymentTerms}
            onChange={setPaymentTerms}
            onValidityChange={setPaymentTermsValid}
            serviceType={lead?.service_type}
          />
        </div>

        <div className="flex gap-3">
          <Button
            onClick={onCancel}
            variant="outline"
            className="flex-1 h-12 text-base font-medium rounded-xl border-2 border-gray-300"
            disabled={converting}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={converting || !parseFloat(projectValue) || !paymentTermsValid}
            className="flex-1 h-12 text-base font-medium rounded-xl bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            data-testid="confirm-convert-btn"
          >
            {converting ? 'Converting...' : 'Create Project'}
          </Button>
        </div>
      </div>
    </div>
  );
}
